import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Clock, Info } from "lucide-react";
import { listMatches, listGymEvents, listGenericEvents, listGymSospensioni } from "../lib/db";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const CATEGORY_STYLES = {
  calcio: {
    label: "⚽ Calcio",
    dot: "bg-yellow-400",
    chip: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30",
    chipActive: "bg-yellow-400 text-zinc-950 border-yellow-400",
  },
  palestra: {
    label: "🏋️ Palestra",
    dot: "bg-emerald-400",
    chip: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
    chipActive: "bg-emerald-400 text-zinc-950 border-emerald-400",
  },
  evento: {
    label: "📅 Eventi",
    dot: "bg-purple-400",
    chip: "bg-purple-400/15 text-purple-300 border-purple-400/30",
    chipActive: "bg-purple-400 text-zinc-950 border-purple-400",
  },
};

// Stili per tipo di partita (dentro la categoria "calcio"), selezionabili in Admin
const MATCH_TYPE_STYLES = {
  "Campionato": {
    label: "⚽ Campionato",
    dot: "bg-red-400",
    chip: "bg-red-400/15 text-red-300 border-red-400/30",
  },
  "Amichevole": {
    label: "⚽ Amichevole",
    dot: "bg-sky-400",
    chip: "bg-sky-400/15 text-sky-300 border-sky-400/30",
  },
  "Torneo (generico)": {
    label: "⚽ Torneo",
    dot: "bg-orange-400",
    chip: "bg-orange-400/15 text-orange-300 border-orange-400/30",
  },
};

function styleFor(ev) {
  if (ev.type === "calcio" && MATCH_TYPE_STYLES[ev.matchType]) return MATCH_TYPE_STYLES[ev.matchType];
  return CATEGORY_STYLES[ev.type];
}

const CANCELLED_CHIP = "bg-zinc-800/60 text-zinc-500 border-zinc-700 line-through";

function toDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CalendarioMensile() {
  const [current, setCurrent] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [matches, setMatches] = useState([]);
  const [gymEvents, setGymEvents] = useState([]);
  const [genericEvents, setGenericEvents] = useState([]);
  const [gymSospensioni, setGymSospensioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState({ calcio: true, palestra: true, evento: true });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    Promise.all([
      listMatches().catch(() => []),
      listGymEvents().catch(() => []),
      listGenericEvents().catch(() => []),
      listGymSospensioni().catch(() => []),
    ])
      .then(([m, g, ge, gs]) => {
        setMatches(m);
        setGymEvents(g);
        setGenericEvents(ge);
        setGymSospensioni(gs);
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleFilter = (cat) => setActiveFilters((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const gridDays = useMemo(() => {
    const year = current.getFullYear();
    const month = current.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = (firstOfMonth.getDay() + 6) % 7;
    const start = new Date(firstOfMonth);
    start.setDate(1 - startOffset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [current]);

  // Sospensioni indicizzate per accesso rapido
  const { fullyClosedDates, suspendedCourseKeys, closureReasonByDate, suspensionReasonByKey } = useMemo(() => {
    const closedDates = new Set();
    const closedReasons = {};
    const courseKeys = new Set();
    const courseReasons = {};
    gymSospensioni.forEach((s) => {
      if (!s.gym_event_id) {
        closedDates.add(s.date);
        if (s.motivo) closedReasons[s.date] = s.motivo;
      } else {
        const key = `${s.gym_event_id}|${s.date}`;
        courseKeys.add(key);
        if (s.motivo) courseReasons[key] = s.motivo;
      }
    });
    return {
      fullyClosedDates: closedDates,
      suspendedCourseKeys: courseKeys,
      closureReasonByDate: closedReasons,
      suspensionReasonByKey: courseReasons,
    };
  }, [gymSospensioni]);

  const eventsByDate = useMemo(() => {
    const map = {};
    const add = (dateKey, ev) => {
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(ev);
    };

    if (activeFilters.calcio) {
      matches.forEach((m) => {
        const d = new Date(m.date);
        const timeStr = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
        add(toDateKey(d), {
          type: "calcio",
          id: `m-${m.id}`,
          date: d,
         title: m.title || (m.home && m.away ? `${m.home} vs ${m.away}` : (m.home || m.away || "Partita")),
          subtitle: m.competition,
          venue: m.venue,
          timeStart: timeStr,
          matchType: m.match_type || "Campionato",
        });
      });
    }

    if (activeFilters.evento) {
      genericEvents.forEach((e) => {
        const rangeStart = new Date(e.event_date + "T00:00:00");
        const rangeEnd = e.event_date_end ? new Date(e.event_date_end + "T00:00:00") : rangeStart;
        const isMultiDay = rangeEnd.getTime() > rangeStart.getTime();

        const cursor = new Date(rangeStart);
        while (cursor <= rangeEnd) {
          const dateKey = toDateKey(cursor);
          add(dateKey, {
            type: "evento",
            id: `e-${e.id}-${dateKey}`,
            date: new Date(cursor),
            title: e.title,
            subtitle: e.description,
            venue: e.location,
            timeStart: e.time_start,
            timeEnd: e.time_end,
            isMultiDay,
            rangeStart,
            rangeEnd,
          });
          cursor.setDate(cursor.getDate() + 1);
        }
      });
    }

    if (activeFilters.palestra && gymEvents.length > 0) {
      gridDays.forEach((d) => {
        // La stagione palestra va da ottobre a giugno.
        // Anche i giorni fuori mese presenti nella griglia rispettano questa regola.
        const gymMonth = d.getMonth();
        const isGymSeasonDay = gymMonth >= 9 || gymMonth <= 5;
        if (!isGymSeasonDay) return;

        const dateKey = toDateKey(d);

        // Palestra chiusa quel giorno: un'unica voce informativa, niente corsi singoli
        if (fullyClosedDates.has(dateKey)) {
          add(dateKey, {
            type: "palestra",
            id: `closed-${dateKey}`,
            date: d,
            title: "Palestra chiusa",
            subtitle: closureReasonByDate[dateKey] || null,
            closed: true,
          });
          return;
        }

        const jsDay = d.getDay();
        const dayOrder = jsDay === 0 ? 7 : jsDay;
        gymEvents
          .filter((g) => g.day_order === dayOrder)
          .forEach((g) => {
            const key = `${g.id}|${dateKey}`;
            const isCancelled = suspendedCourseKeys.has(key);
            add(dateKey, {
              type: "palestra",
              id: `g-${g.id}-${dateKey}`,
              date: d,
              title: g.course_name,
              subtitle: isCancelled ? (suspensionReasonByKey[key] || "Sospeso") : g.instructor,
              timeStart: g.time_start,
              timeEnd: g.time_end,
              cancelled: isCancelled,
            });
          });
      });
    }

    Object.values(map).forEach((list) =>
      list.sort((a, b) => (a.timeStart || "00:00").localeCompare(b.timeStart || "00:00"))
    );
    return map;
  }, [matches, gymEvents, genericEvents, activeFilters, gridDays, fullyClosedDates, suspendedCourseKeys, closureReasonByDate, suspensionReasonByKey]);

  const todayKey = toDateKey(new Date());
  const currentMonthIndex = current.getMonth();

  const goToPrevMonth = () =>
    setCurrent((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  const goToNextMonth = () =>
    setCurrent((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  const goToday = () => {
    const d = new Date();
    d.setDate(1);
    setCurrent(d);
  };

  const agendaItems = useMemo(() => {
    const items = [];
    Object.entries(eventsByDate).forEach(([dateKey, evs]) => {
      const d = new Date(dateKey + "T00:00:00");
      if (d.getMonth() === currentMonthIndex && d.getFullYear() === current.getFullYear()) {
        evs.forEach((ev) => items.push(ev));
      }
    });
    return items.sort((a, b) => a.date - b.date || (a.timeStart || "").localeCompare(b.timeStart || ""));
  }, [eventsByDate, currentMonthIndex, current]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={goToPrevMonth}
            className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-colors"
            aria-label="Mese precedente"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h2 className="font-display font-bold text-xl sm:text-2xl uppercase tracking-tight min-w-[180px] text-center">
            {MONTH_NAMES[currentMonthIndex]} {current.getFullYear()}
          </h2>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-colors"
            aria-label="Mese successivo"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <button onClick={goToday} className="text-xs font-display font-bold uppercase tracking-wider text-yellow-400 hover:underline">
          Oggi
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
          <button
            key={key}
            onClick={() => toggleFilter(key)}
            className={`px-4 py-1.5 rounded-full text-xs font-display font-bold uppercase tracking-wider border transition-colors ${
              activeFilters[key] ? style.chipActive : "bg-zinc-900 border-zinc-800 text-zinc-500 hover:border-zinc-600"
            }`}
          >
            {style.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-lg bg-zinc-900 animate-pulse" />
          ))}
        </div>
      ) : isMobile ? (
        <AgendaView items={agendaItems} onSelect={setSelectedEvent} />
      ) : (
        <GridView
          gridDays={gridDays}
          currentMonthIndex={currentMonthIndex}
          eventsByDate={eventsByDate}
          todayKey={todayKey}
          onSelect={setSelectedEvent}
        />
      )}

      <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
}

function chipClassFor(ev) {
  if (ev.cancelled || ev.closed) return CANCELLED_CHIP;
  return styleFor(ev).chip;
}

function GridView({ gridDays, currentMonthIndex, eventsByDate, todayKey, onSelect }) {
  return (
    <div className="rounded-2xl border border-zinc-800 overflow-hidden bg-zinc-900">
      <div className="grid grid-cols-7 border-b border-zinc-800">
        {DAY_NAMES.map((d) => (
          <div key={d} className="py-2.5 text-center text-[11px] font-display font-bold uppercase tracking-wider text-zinc-500">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {gridDays.map((d) => {
          const dateKey = toDateKey(d);
          const isCurrentMonth = d.getMonth() === currentMonthIndex;
          const isToday = dateKey === todayKey;
          const dayEvents = eventsByDate[dateKey] || [];
          return (
            <div
              key={dateKey}
              className={`min-h-[90px] sm:min-h-[110px] border-b border-r border-zinc-800 p-1.5 sm:p-2 [&:nth-child(7n)]:border-r-0 ${
                isCurrentMonth ? "" : "bg-zinc-950/60"
              }`}
            >
              <div
                className={`text-xs font-display font-bold mb-1 inline-flex items-center justify-center h-6 w-6 rounded-full ${
                  isToday ? "bg-yellow-400 text-zinc-950" : isCurrentMonth ? "text-zinc-300" : "text-zinc-700"
                }`}
              >
                {d.getDate()}
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => onSelect(ev)}
                    className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] sm:text-[11px] font-medium truncate border ${chipClassFor(ev)}`}
                    title={ev.title}
                  >
                    {(ev.cancelled || ev.closed) && "🚫 "}{ev.isMultiDay && !ev.cancelled && !ev.closed && "↔ "}{ev.title}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[10px] text-zinc-500 pl-1.5">+{dayEvents.length - 3} altro</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgendaView({ items, onSelect }) {
  if (items.length === 0) {
    return <p className="text-zinc-500 text-sm text-center py-12">Nessun evento in questo mese.</p>;
  }
  return (
    <div className="space-y-2">
      {items.map((ev) => (
        <button
          key={ev.id}
          onClick={() => onSelect(ev)}
          className={`w-full text-left rounded-xl bg-zinc-900 border p-4 flex items-center gap-4 transition-colors ${
            ev.cancelled || ev.closed ? "border-zinc-800/60 opacity-60" : "border-zinc-800 hover:border-zinc-600"
          }`}
        >
          <div className="h-11 w-11 rounded-lg bg-zinc-950 border border-zinc-800 flex flex-col items-center justify-center shrink-0">
            <span className="text-[9px] text-zinc-500 uppercase leading-none">
              {ev.date.toLocaleDateString("it-IT", { month: "short" })}
            </span>
            <span className="text-base font-black leading-none mt-0.5">{ev.date.getDate()}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full shrink-0 ${ev.cancelled || ev.closed ? "bg-zinc-600" : styleFor(ev).dot}`} />
              <div className={`font-display font-bold text-sm truncate ${ev.cancelled || ev.closed ? "line-through text-zinc-500" : ""}`}>
                {(ev.cancelled || ev.closed) && "🚫 "}{ev.isMultiDay && !ev.cancelled && !ev.closed && "↔ "}{ev.title}
              </div>
            </div>
            {(ev.timeStart || ev.subtitle) && (
              <div className="text-xs text-zinc-500 mt-0.5 truncate">
                {ev.timeStart && `ore ${ev.timeStart.slice(0, 5)}`}
                {ev.timeStart && ev.subtitle ? " • " : ""}
                {ev.subtitle}
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

function EventModal({ event, onClose }) {
  if (!event) return null;
  const isCancelled = event.cancelled || event.closed;
  const style = styleFor(event);
  return (
    <Dialog open={!!event} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border w-fit ${isCancelled ? CANCELLED_CHIP : style.chip}`}>
            {isCancelled ? "🚫 Sospeso" : style.label}
          </span>
          <DialogTitle className={`font-display uppercase tracking-tight text-xl mt-2 ${isCancelled ? "line-through text-zinc-500" : ""}`}>
            {event.title}
          </DialogTitle>
          {event.subtitle && (
            <DialogDescription className={isCancelled ? "text-zinc-500 flex items-center gap-1.5" : "text-zinc-400"}>
              {isCancelled && <Info className="h-3.5 w-3.5 shrink-0" />}
              {event.subtitle}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-2 text-sm text-zinc-300">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-zinc-500 shrink-0" />
            {event.isMultiDay ? (
              <span>
                Dal {event.rangeStart.toLocaleDateString("it-IT", { day: "numeric", month: "long", timeZone: "Europe/Rome" })}
                {" "}al {event.rangeEnd.toLocaleDateString("it-IT", { day: "numeric", month: "long", timeZone: "Europe/Rome" })}
                {event.timeStart && ` • ore ${event.timeStart.slice(0, 5)}${event.timeEnd ? `–${event.timeEnd.slice(0, 5)}` : ""}`}
              </span>
            ) : (
              <span>
                {event.date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Rome" })}
                {event.timeStart && ` • ore ${event.timeStart.slice(0, 5)}${event.timeEnd ? `–${event.timeEnd.slice(0, 5)}` : ""}`}
              </span>
            )}
          </div>
          {event.venue && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-zinc-500 shrink-0" />
              {event.venue}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}